import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AccessibilityWidget.css';

const STORAGE_KEY = 'a11y-settings';

const DEFAULT_SETTINGS = {
  fontSize: 100,
  highContrast: false,
  highlightLinks: false,
  dyslexiaFont: false,
  reducedMotion: false,
};

function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : { ...DEFAULT_SETTINGS };
    } catch {
      return { ...DEFAULT_SETTINGS };
    }
  });

  const panelRef = useRef(null);
  const toggleRef = useRef(null);

  // Persist settings
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch { /* storage full or blocked */ }
  }, [settings]);

  // Apply settings to DOM
  useEffect(() => {
    const html = document.documentElement;

    // Font size
    html.style.fontSize = settings.fontSize === 100 ? '' : `${settings.fontSize}%`;

    // Toggle classes
    html.classList.toggle('a11y-high-contrast', settings.highContrast);
    html.classList.toggle('a11y-highlight-links', settings.highlightLinks);
    html.classList.toggle('a11y-dyslexia-font', settings.dyslexiaFont);
    html.classList.toggle('a11y-reduced-motion', settings.reducedMotion);
  }, [settings]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        toggleRef.current && !toggleRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const update = useCallback((key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetAll = useCallback(() => {
    setSettings({ ...DEFAULT_SETTINGS });
    document.documentElement.style.fontSize = '';
  }, []);

  const adjustFontSize = useCallback((delta) => {
    setSettings((prev) => {
      const next = Math.min(150, Math.max(80, prev.fontSize + delta));
      return { ...prev, fontSize: next };
    });
  }, []);

  return (
    <>
      <button
        ref={toggleRef}
        className="a11y-widget__toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close accessibility settings' : 'Open accessibility settings'}
        aria-expanded={open}
        type="button"
      >
        {/* Wheelchair / universal access icon */}
        <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28" aria-hidden="true">
          <path d="M12 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4Zm9 5H3v2h6.5v4l-2 7h2.2l1.8-6.3L13.3 20h2.2l-2-7V9H20V7Z"/>
        </svg>
      </button>

      <div
        ref={panelRef}
        className={`a11y-widget__panel${open ? ' a11y-widget__panel--open' : ''}`}
        role="dialog"
        aria-label="Accessibility settings"
        aria-hidden={!open}
      >
        <h2 className="a11y-widget__title">Accessibility</h2>

        <div className="a11y-widget__controls">
          {/* Font Size */}
          <div className="a11y-widget__row">
            <span className="a11y-widget__label">Font Size</span>
            <div className="a11y-widget__font-controls">
              <button
                className="a11y-widget__font-btn"
                onClick={() => adjustFontSize(-10)}
                aria-label="Decrease font size"
                type="button"
              >
                A&minus;
              </button>
              <span className="a11y-widget__font-value">{settings.fontSize}%</span>
              <button
                className="a11y-widget__font-btn"
                onClick={() => adjustFontSize(10)}
                aria-label="Increase font size"
                type="button"
              >
                A+
              </button>
            </div>
          </div>

          <hr className="a11y-widget__divider" />

          {/* High Contrast */}
          <div className="a11y-widget__row">
            <span className="a11y-widget__label">High Contrast</span>
            <label className="a11y-widget__switch">
              <input
                type="checkbox"
                checked={settings.highContrast}
                onChange={(e) => update('highContrast', e.target.checked)}
                aria-label="Toggle high contrast"
              />
              <span className="a11y-widget__slider" />
            </label>
          </div>

          {/* Highlight Links */}
          <div className="a11y-widget__row">
            <span className="a11y-widget__label">Highlight Links</span>
            <label className="a11y-widget__switch">
              <input
                type="checkbox"
                checked={settings.highlightLinks}
                onChange={(e) => update('highlightLinks', e.target.checked)}
                aria-label="Toggle highlight links"
              />
              <span className="a11y-widget__slider" />
            </label>
          </div>

          {/* Dyslexia Font */}
          <div className="a11y-widget__row">
            <span className="a11y-widget__label">Dyslexia Font</span>
            <label className="a11y-widget__switch">
              <input
                type="checkbox"
                checked={settings.dyslexiaFont}
                onChange={(e) => update('dyslexiaFont', e.target.checked)}
                aria-label="Toggle dyslexia-friendly font"
              />
              <span className="a11y-widget__slider" />
            </label>
          </div>

          {/* Reduce Motion */}
          <div className="a11y-widget__row">
            <span className="a11y-widget__label">Reduce Motion</span>
            <label className="a11y-widget__switch">
              <input
                type="checkbox"
                checked={settings.reducedMotion}
                onChange={(e) => update('reducedMotion', e.target.checked)}
                aria-label="Toggle reduced motion"
              />
              <span className="a11y-widget__slider" />
            </label>
          </div>

          <hr className="a11y-widget__divider" />

          {/* Reset */}
          <button
            className="a11y-widget__reset"
            onClick={resetAll}
            type="button"
          >
            Reset All
          </button>
        </div>
      </div>
    </>
  );
}

export default AccessibilityWidget;
